import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';
import {
  Link,
  SandboxView,
  SandboxedLaunchRequest,
  SandboxedModel,
  SandboxedPresetInfo,
} from '@internal/plugin-sandboxed-common';

export class SandboxedApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly issues: { path?: string; message: string }[] = [],
  ) {
    super(message);
    this.name = 'SandboxedApiError';
  }
}

export interface SandboxedApi {
  listPresets(entityRef: string): Promise<SandboxedPresetInfo[]>;
  /** Empty when the gateway is unconfigured or unreachable. */
  listModels(entityRef: string): Promise<SandboxedModel[]>;
  listSandboxes(entityRef: string): Promise<SandboxView[]>;
  getSandbox(entityRef: string, id: string): Promise<SandboxView>;
  /** One object rather than three arguments — it is the POST body. */
  launch(request: SandboxedLaunchRequest): Promise<SandboxView>;
  end(entityRef: string, id: string): Promise<SandboxView>;
  openTerminal(entityRef: string, id: string): Promise<Link>;
  openPreview(entityRef: string, id: string, port: number): Promise<Link>;
}

export const sandboxedApiRef = createApiRef<SandboxedApi>({
  id: 'plugin.sandboxed.api',
});

export class SandboxedClient implements SandboxedApi {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  private async call<T>(
    method: 'GET' | 'POST' | 'DELETE',
    path: string,
    options: { query?: Record<string, string>; body?: unknown } = {},
  ): Promise<T> {
    const base = await this.discoveryApi.getBaseUrl('sandboxed');
    const search = options.query
      ? `?${new URLSearchParams(options.query).toString()}`
      : '';

    const response = await this.fetchApi.fetch(`${base}${path}${search}`, {
      method,
      ...(options.body === undefined
        ? {}
        : {
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify(options.body),
          }),
    });

    if (!response.ok) {
      // The router answers `{error, issues?}`; anything else is a proxy or a
      // crash, and the status line is what there is to say.
      let message = `${response.status} ${response.statusText}`;
      let issues: { path?: string; message: string }[] = [];
      try {
        const body = await response.json();
        if (typeof body?.error === 'string') message = body.error;
        if (Array.isArray(body?.issues)) issues = body.issues;
      } catch {
        /* keep the status line */
      }
      throw new SandboxedApiError(message, response.status, issues);
    }

    return response.json();
  }

  async listPresets(entityRef: string): Promise<SandboxedPresetInfo[]> {
    const body = await this.call<{ presets?: SandboxedPresetInfo[] }>(
      'GET',
      '/presets',
      { query: { entityRef } },
    );
    return body.presets ?? [];
  }

  async listModels(entityRef: string): Promise<SandboxedModel[]> {
    const body = await this.call<{ models?: SandboxedModel[] }>(
      'GET',
      '/models',
      { query: { entityRef } },
    );
    return body.models ?? [];
  }

  async listSandboxes(entityRef: string): Promise<SandboxView[]> {
    const body = await this.call<{ sandboxes?: SandboxView[] }>(
      'GET',
      '/sandboxes',
      { query: { entityRef } },
    );
    return body.sandboxes ?? [];
  }

  async getSandbox(entityRef: string, id: string): Promise<SandboxView> {
    return this.call('GET', `/sandboxes/${encodeURIComponent(id)}`, {
      query: { entityRef },
    });
  }

  async launch(request: SandboxedLaunchRequest): Promise<SandboxView> {
    return this.call('POST', '/sandboxes', { body: request });
  }

  async end(entityRef: string, id: string): Promise<SandboxView> {
    return this.call('DELETE', `/sandboxes/${encodeURIComponent(id)}`, {
      query: { entityRef },
    });
  }

  async openTerminal(entityRef: string, id: string): Promise<Link> {
    return this.call('POST', `/sandboxes/${encodeURIComponent(id)}/terminal`, {
      body: { entityRef },
    });
  }

  async openPreview(
    entityRef: string,
    id: string,
    port: number,
  ): Promise<Link> {
    return this.call('POST', `/sandboxes/${encodeURIComponent(id)}/preview`, {
      body: { entityRef, port },
    });
  }
}
